/**
 * net/LinkRequestClient.ts — o lado DISPOSITIVO do vínculo por QR (TV-QR-APPROVAL §3).
 *
 *   POST /screens/link-requests            (ANÔNIMO — a TV não tem conta)
 *     → { requestId, requestSecret, qrPayload, fallbackUrl, confirmationCode, expiresAt, pollIntervalSeconds }
 *   GET  /screens/link-requests/:id        (header X-Link-Secret, a cada N s)
 *     → {status:'pending'} … {status:'approved', deviceToken, screen}
 *
 * REGRAS QUE ESTE ARQUIVO NÃO PODE ERRAR (mesma lista do `PairingClient` que ele
 * substitui):
 *  1. **Nada aqui concede sozinho.** Sem `approved` de um celular logado, a
 *     solicitação expira em 5 min e não cria tela nenhuma (TV-QR §10.1).
 *  2. **O segredo (`ls_`) é a credencial real do dispositivo NESTA sessão.** Vai
 *     no HEADER e nunca em URL/QR/tela/log. O QR carrega só o `requestId`, que
 *     não é segredo (TV-QR §3.1).
 *  3. **`404`/`401 INVALID_LINK_SECRET` geram solicitação NOVA sozinhas** — a TV
 *     nunca fica muda.
 *  4. **`denied` e `expired` PARAM e EXPLIQUAM** (plano §3.9). Renovar em
 *     silêncio seria insistir com quem acabou de dizer "não" — e o rate limit
 *     de 20/h por IP queimaria a cota de todo mundo atrás do mesmo NAT. A casca
 *     mostra o motivo e oferece "pedir de novo".
 *  5. **`429` é backoff, nunca crash** — retoma a MESMA solicitação.
 *  6. **A credencial é gravada ANTES de avisar a casca** (a aba pode fechar).
 *
 * ponytail: `fetch`/sleep/clock injetáveis (mesma técnica do `PairingClient`)
 * para o teste cobrir 401/410/429/denied sem backend. Sem SSE, sem long-poll: o
 * §3.2 marca `?wait=` como endurecimento de fase futura.
 */
import { GatewayError, codeFromErrorEnvelope } from './errors';
import { backoffDelayMs } from './backoff';
import type { ScreenCredentials, KeyValueStore } from './credentials';
import { CredentialStore } from './credentials';
import { DEFAULT_API_BASE_URL } from './config';
import type { LinkRequestStatus } from './types';
import { systemClock, type Clock } from './clock';

/** O que a tela de vínculo desenha (QR + código de conferência + URL). */
export interface LinkDisplay {
  requestId: string;
  qrPayload: string;
  confirmationCode: string;
  fallbackUrl: string;
  expiresAt: string;
  pollIntervalSeconds: number;
}

export type LinkRequestState =
  /** Solicitação viva, esperando o celular logado aprovar. */
  | { status: 'waiting'; display: LinkDisplay }
  /** O backend encerrou a solicitação; uma nova está sendo criada. */
  | { status: 'renewing'; reason: string }
  /** Aprovado: a credencial já foi gravada em disco. */
  | { status: 'approved'; credentials: ScreenCredentials }
  /** O celular logado RECUSOU esta tela — terminal, não renova sozinho. */
  | { status: 'denied' }
  /** A solicitação venceu (5 min) — para e EXPLICA; a TV pode pedir outra. */
  | { status: 'expired' }
  /** Sem rede: o vínculo não é offline, mas a TV não morre por isso. */
  | { status: 'offline'; message: string };

export interface LinkRequestClientOptions {
  baseUrl?: string;
  deviceLabel?: string;
  appVersion?: string;
  platform?: 'web' | 'android' | 'androidtv';
  store: KeyValueStore;
  clock?: Clock;
  fetchImpl?: (input: string, init?: RequestInit) => Promise<Response>;
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
  onLog?: (message: string, detail?: unknown) => void;
}

export class LinkRequestClient {
  private readonly baseUrl: string;
  private readonly store: CredentialStore;
  private readonly clock: Clock;
  private readonly doFetch: (input: string, init?: RequestInit) => Promise<Response>;
  private readonly sleep: (ms: number, signal?: AbortSignal) => Promise<void>;
  private readonly log: (message: string, detail?: unknown) => void;
  private readonly options: LinkRequestClientOptions;

  private request: { requestId: string; requestSecret: string; display: LinkDisplay } | null = null;
  private attempt = 0;
  private stopped = false;

  constructor(options: LinkRequestClientOptions) {
    this.options = options;
    this.baseUrl = (options.baseUrl ?? DEFAULT_API_BASE_URL).replace(/\/+$/, '');
    this.store = new CredentialStore(options.store);
    this.clock = options.clock ?? systemClock;
    this.doFetch = options.fetchImpl ?? ((input, init) => fetch(input, init));
    this.sleep =
      options.sleep ??
      ((ms, signal) =>
        new Promise<void>((resolve, reject) => {
          const timer = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
          }, ms);
          const onAbort = (): void => {
            clearTimeout(timer);
            reject(new Error('abortado'));
          };
          if (signal?.aborted) {
            onAbort();
            return;
          }
          signal?.addEventListener('abort', onAbort, { once: true });
        }));
    this.log = options.onLog ?? (() => {});
  }

  loadCredentials(): ScreenCredentials | null {
    return this.store.load();
  }

  forget(): void {
    this.store.clear();
  }

  private url(path: string): string {
    return `${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  }

  private async readError(response: Response): Promise<GatewayError> {
    let envelopeCode: string | undefined;
    let message = `HTTP ${response.status}`;
    try {
      const body = (await response.clone().json()) as { error?: { code?: string; message?: string } };
      if (body?.error) {
        if (typeof body.error.code === 'string') envelopeCode = body.error.code;
        if (typeof body.error.message === 'string') message = body.error.message;
      }
    } catch {
      // sem envelope: decide pelo status (nunca pela raspagem de texto)
    }
    return new GatewayError(codeFromErrorEnvelope(envelopeCode, response.status), message, response.status);
  }

  /** Cria a solicitação anônima. ÚNICA chamada sem credencial desta frente. */
  private async createRequest(): Promise<void> {
    const response = await this.doFetch(this.url('/screens/link-requests'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        platform: this.options.platform ?? 'web',
        appVersion: this.options.appVersion ?? '0.0.0-web',
        ...(this.options.deviceLabel ? { deviceLabel: this.options.deviceLabel } : {}),
      }),
    });
    if (!response.ok) {
      throw await this.readError(response);
    }
    const body = (await response.json()) as {
      requestId: string;
      requestSecret: string;
      qrPayload: string;
      fallbackUrl: string;
      confirmationCode: string;
      expiresAt: string;
      pollIntervalSeconds: number;
    };
    if (!body?.requestId || !body?.requestSecret || !body?.qrPayload) {
      throw new GatewayError('BAD_RESPONSE', 'Solicitação de vínculo inválida.', response.status);
    }
    this.request = {
      requestId: body.requestId,
      requestSecret: body.requestSecret,
      display: {
        requestId: body.requestId,
        qrPayload: body.qrPayload,
        confirmationCode: body.confirmationCode ?? '',
        fallbackUrl: body.fallbackUrl ?? '',
        expiresAt: body.expiresAt,
        pollIntervalSeconds:
          typeof body.pollIntervalSeconds === 'number' && body.pollIntervalSeconds > 0
            ? body.pollIntervalSeconds
            : 3,
      },
    };
    this.attempt = 0;
    // O requestId NÃO é segredo e o secret NUNCA é logado (TV-QR §10.6).
    this.log('solicitação de vínculo criada', { requestId: body.requestId });
  }

  /** Um poll. `pending` volta como valor; o resto LANÇA para a política do `run`. */
  private async pollOnce(): Promise<
    | { status: 'pending' }
    | { status: 'approved'; deviceToken: string; screen: { id: string; name?: string } }
    | { status: 'denied' | 'expired' }
  > {
    const active = this.request;
    if (!active) {
      throw new GatewayError('NOT_FOUND', 'Sem solicitação de vínculo ativa.');
    }
    const response = await this.doFetch(this.url(`/screens/link-requests/${active.requestId}`), {
      method: 'GET',
      // Header — e NÃO query string: o segredo nunca vai para log de proxy (TV-QR §3.2).
      headers: { 'X-Link-Secret': active.requestSecret },
    });
    if (!response.ok) {
      throw await this.readError(response);
    }
    const body = (await response.json()) as {
      status: LinkRequestStatus;
      deviceToken?: string;
      screen?: { id: string; name?: string };
    };
    if (body.status === 'approved' && body.deviceToken && body.screen) {
      return { status: 'approved', deviceToken: body.deviceToken, screen: body.screen };
    }
    if (body.status === 'denied' || body.status === 'expired') {
      // Terminal para o LOOP (plano §3.9): a tela explica e o balcão decide.
      // Renovar em silêncio aqui seria insistir com quem acabou de dizer "não".
      return { status: body.status };
    }
    return { status: 'pending' };
  }

  /**
   * Loop de vínculo. Roda até ser aprovado, recusado, expirado ou abortado,
   * emitindo estado a cada transição.
   *
   * QUANDO RENOVA SOZINHO: `404` e `401 INVALID_LINK_SECRET` — a solicitação
   * morreu e um QR novo é o comportamento esperado da TV. QUANDO PARA:
   * `denied` e `expired` (a casca mostra o motivo e oferece "pedir de novo");
   * `429` recua e retoma a mesma; `NETWORK` recua e continua.
   */
  async run(onState: (state: LinkRequestState) => void, signal?: AbortSignal): Promise<void> {
    this.stopped = false;
    while (!this.stopped && !signal?.aborted) {
      try {
        if (!this.request) {
          await this.createRequest();
        }
        const active = this.request;
        if (!active) {
          throw new GatewayError('NOT_FOUND', 'Sem solicitação de vínculo ativa.');
        }
        onState({ status: 'waiting', display: active.display });

        const result = await this.pollOnce();
        if (result.status === 'approved') {
          const credentials: ScreenCredentials = {
            deviceToken: result.deviceToken,
            screenId: result.screen.id,
            pairedAt: new Date(this.clock.now()).toISOString(),
            ...(result.screen.name ? { screenName: result.screen.name } : {}),
          };
          // GRAVA ANTES de avisar a casca: se a aba fechar logo depois, o token
          // tem que já estar em disco (mesma técnica do `PairingClient`).
          this.store.save(credentials);
          onState({ status: 'approved', credentials });
          return;
        }
        if (result.status === 'denied' || result.status === 'expired') {
          this.request = null;
          onState({ status: result.status });
          return;
        }

        // `pending`: espera o intervalo que o SERVIDOR ditou e faz o próximo poll.
        await this.sleep(active.display.pollIntervalSeconds * 1000, signal);
      } catch (error) {
        if (signal?.aborted) {
          return;
        }
        const failure = error instanceof GatewayError ? error : new GatewayError('NETWORK', String(error));

        if (failure.code === 'LINK_REQUEST_EXPIRED' || failure.code === 'NOT_FOUND' || failure.code === 'DEVICE_TOKEN_INVALID' || failure.code === 'INVALID_LINK_SECRET') {
          // Solicitação/commit gone: larga e cria outra, sem interação do
          // usuário — a TV nunca fica muda (TV-QR §7).
          this.request = null;
          onState({ status: 'renewing', reason: failure.code });
          continue;
        }
        if (failure.code === 'RATE_LIMITED') {
          // Backoff educado, nunca crash. A janela é por operação: a solicitação
          // atual pode seguir válida, então só o poll é retomado.
          const delay = backoffDelayMs(this.attempt);
          this.attempt += 1;
          this.log('vínculo limitado — recuando', { attempt: this.attempt, delay });
          try {
            await this.sleep(delay, signal);
          } catch {
            return;
          }
          continue;
        }
        if (failure.code === 'NETWORK') {
          // Sem rede: o vínculo não é offline, mas a TV não pode morrer por isso.
          onState({ status: 'offline', message: 'Sem internet. Conectando...' });
          const delay = backoffDelayMs(this.attempt);
          this.attempt += 1;
          try {
            await this.sleep(delay, signal);
          } catch {
            return;
          }
          continue;
        }
        // `BAD_RESPONSE` e afins: contrato furado. Não é para retry cego.
        throw failure;
      }
    }
  }

  /** Zera a solicitação atual — a casca chama antes de "pedir de novo". */
  reset(): void {
    this.request = null;
    this.attempt = 0;
  }

  stop(): void {
    this.stopped = true;
  }

  /** Exibição atual (ou `null` se ainda não há solicitação) — a UI de vínculo lê. */
  currentDisplay(): LinkDisplay | null {
    return this.request?.display ?? null;
  }
}
